import React, { useEffect, useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Gift, Users, TrendingUp, Award, Share2, User, Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import ReferralCode from "@/components/ReferralCode";
import { useToast } from "@/components/ui/use-toast";
import supabase from "@/utils/supabaseClient";
import { useQuery } from "@tanstack/react-query";
import {Session} from "@supabase/supabase-js";
import {SEO} from "@/components/SEO.tsx";
import {useAuth} from "@/contexts/AuthContext.tsx";

const Referral = () => {
  const [referralCode, setReferralCode] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [referralsCount, setReferralsCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { toast } = useToast();
  /*const [userSession] = useState<Session | null>(() => {
    const cachedSession = localStorage.getItem('session');
    return cachedSession ? JSON.parse(cachedSession) : null;
  });*/
  const {user, session, loading} = useAuth();
  const { data: userData } = useQuery({
    queryKey: ['users',session],
    queryFn: async () => {
      if (session) {
        const url = `${import.meta.env.VITE_API_URL}/users`;
        const headers = {
          'Authorization': `Bearer ${session.access_token}`
        };
        const response = await fetch(url, {
          method: "GET",
          headers,
          redirect: "follow"
        });
        return response.json();
      }
      return null;
    },
    enabled: !!user
  });

  const { data: referralData } = useQuery({
    queryKey: ['referrals',session],
    queryFn: async () => {
      if (session) {
        const url = `${import.meta.env.VITE_API_URL}/users/referrals`;
        const headers = {
          'Authorization': `Bearer ${session.access_token}`
        };
        const response = await fetch(url, {
          method: "GET",
          headers,
          redirect: "follow"
        });

        const responseData = await response.json();
        // console.log("responseData:", responseData)
        return responseData;
      }
      return null;
    },
    enabled: !!user
  });

  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setIsLoggedIn(true);
        // Fetch referral count
        /*const { data: userData, error } = await supabase
          .from('users')
          .select('referral_count')
          .eq('id', data.session.user.id)
          .single();

        if (userData) {
          setReferralsCount(userData.referral_count || 0);
        }*/
      }
    };

    checkSession();
  }, []);

  useEffect(() => {
    if (userData?.data?.userCode) {
      setReferralCode(userData.data.userCode);
    }
  }, [userData]);

  const handleShareClick = async () => {
    try {
      const referralUrl = `${import.meta.env.VITE_SITE_URL}/signup?r=${referralCode}`;
      await navigator.share({
        title: 'Join me on BlankPoll',
        text: 'Use my referral link to sign up for BlankPoll and we both get rewards!',
        url: referralUrl,
      });
    } catch (err) {
      // If Web Share API is not available, copy to clipboard instead
      try {
        const referralUrl = `${import.meta.env.VITE_SITE_URL}/signup?r=${referralCode}`;
        await navigator.clipboard.writeText(referralUrl);
        setCopied(true);
        toast({
          title: "Link copied!",
          description: "Share it with your friends to earn rewards.",
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // console.error("Failed to copy:", err);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
          title="Referral"
          description="Invite friends, earn rewards, and grow the community together"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />

      <main className="flex-grow">
        <section className="bg-gradient-to-b from-[#f8f9fa] to-white dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-secondary dark:text-white mt-6">
                Referral Program
              </h1>
              <p className="text-xl text-secondary-light dark:text-gray-300 mb-8">
                Share the experience, multiply the rewards.
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <h2 className="text-3xl font-semibold mb-6 text-secondary dark:text-white">How It Works</h2>

              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="bg-[#96A58D]/10 dark:bg-[#2C3930]/20 p-3 rounded-full h-fit">
                    <Share2 className="w-6 h-6 text-[#96A58D] dark:text-[#2C3930]/90" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2 text-secondary dark:text-white">Share Your Referral Link</h3>
                    <p className="text-secondary-light dark:text-gray-300">
                      Invite friends, family, or colleagues to join BlankPoll using your unique referral link.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-[#96A58D]/10 dark:bg-[#2C3930]/20 p-3 rounded-full h-fit">
                    <User className="w-6 h-6 text-[#96A58D] dark:text-[#2C3930]/90" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2 text-secondary dark:text-white">They Join BlankPoll</h3>
                    <p className="text-secondary-light dark:text-gray-300">
                      When they sign up through your link, they automatically become part of your referral network.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-[#96A58D]/10 dark:bg-[#2C3930]/20 p-3 rounded-full h-fit">
                    <Gift className="w-6 h-6 text-[#96A58D] dark:text-[#2C3930]/90" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2 text-secondary dark:text-white">Earn Rewards</h3>
                    <p className="text-secondary-light dark:text-gray-300">
                      Receive 100 bp for each new user who joins through your referral link.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Card className="shadow-lg border-t-4 border-t-[#96A58D] dark:border-t-[#2C3930]">
                <CardHeader>
                  <CardTitle className="text-2xl">Your Referral Dashboard</CardTitle>
                  <CardDescription>Share your code to start earning rewards</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!!user ? (
                    <>
                      <div className="p-4 bg-[#96A58D]/10 dark:bg-[#2C3930]/20 rounded-lg">
                        <p className="font-medium text-sm text-secondary-light dark:text-gray-300 mb-2">
                          Your Referral Code:
                        </p>
                        <ReferralCode code={referralCode} copied={copied} setCopied={setCopied} />
                      </div>

                      <div className="flex justify-center">
                        <Button
                          onClick={handleShareClick}
                          className="w-full bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                        >
                          {copied ? <Check className="w-5 h-5 mr-2" /> : <Share2 className="w-5 h-5 mr-2" />}
                          {copied ? "Copied!" : "Share Your Link"}
                        </Button>
                      </div>

                      <Separator />

                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <Users className="w-6 h-6 mx-auto mb-2 text-[#96A58D] dark:text-[#2C3930]/90" />
                          <p className="text-sm text-secondary-light dark:text-gray-300">Referrals</p>
                          <p className="text-2xl font-bold text-secondary dark:text-white">{referralData?.totalReferrals}</p>
                        </div>
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <Gift className="w-6 h-6 mx-auto mb-2 text-[#96A58D] dark:text-[#2C3930]/90" />
                          <p className="text-sm text-secondary-light dark:text-gray-300">Earned</p>
                          <p className="text-2xl font-bold text-secondary dark:text-white">{referralData?.totalPoints} bp</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="mb-4 text-secondary-light dark:text-gray-300">
                        Please log in to view your referral code and start earning rewards.
                      </p>
                      <Button
                        onClick={() => window.location.href = '/login'}
                        className="bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                      >
                        Log In
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-50 dark:bg-gray-800/50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-semibold mb-10 text-center text-secondary dark:text-white">
                Referral Benefits
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                <Card className="border-none shadow-md hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 bg-[#96A58D]/10 dark:bg-[#2C3930]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Gift className="w-8 h-8 text-[#96A58D] dark:text-[#2C3930]/90" />
                    </div>
                    <CardTitle className="text-xl">Immediate Rewards</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-secondary-light dark:text-gray-300">
                      Earn <span className="font-bold">100 bp</span> instantly for each new user who joins through your referral link.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-md hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 bg-[#96A58D]/10 dark:bg-[#2C3930]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-[#96A58D] dark:text-[#2C3930]/90" />
                    </div>
                    <CardTitle className="text-xl">Passive Income</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-secondary-light dark:text-gray-300">
                      <span className="font-bold">Coming Soon:</span> Earn 1.25% of the points your referrals win in polls with correct answers.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-md hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 bg-[#96A58D]/10 dark:bg-[#2C3930]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="w-8 h-8 text-[#96A58D] dark:text-[#2C3930]/90" />
                    </div>
                    <CardTitle className="text-xl">No Limits</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-secondary-light dark:text-gray-300">
                      There's no cap on how many people you can refer or how many rewards you can earn.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-semibold mb-6 text-secondary dark:text-white">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6 text-left mt-8">
              <div>
                <h3 className="text-xl font-medium mb-2 text-secondary dark:text-white">How do referral rewards work?</h3>
                <p className="text-secondary-light dark:text-gray-300">
                  When someone signs up using your referral link, you'll automatically receive 100 bp in your account once their registration is complete.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium mb-2 text-secondary dark:text-white">When will the 1.25% ongoing rewards be available?</h3>
                <p className="text-secondary-light dark:text-gray-300">
                  The feature that lets you earn 1.25% of your referrals' earnings from correct poll answers is coming soon. We'll notify all users when this feature goes live.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium mb-2 text-secondary dark:text-white">Are there any restrictions on who I can refer?</h3>
                <p className="text-secondary-light dark:text-gray-300">
                  You can refer anyone who hasn't already created an account with BlankPoll. Self-referrals or creating multiple accounts is against our terms of service.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium mb-2 text-secondary dark:text-white">How can I track my referrals?</h3>
                <p className="text-secondary-light dark:text-gray-300">
                  You can see how many successful referrals you've made and how many rewards you've earned directly from your referral dashboard on this page when you're logged in.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Referral;
